import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { config } from '../../config';
import {
  addDiscoveryHypothesis,
  canCapture,
  commitIncrement,
  loadDiscovery,
  prepareIncrement,
} from '../../app/discovery';
import type { DiscoveryHypothesisView } from '../../app/discovery';
import { newId } from '../../app/ids';
import { log } from '../../app/logger';
import { applyLiveIncrement } from '../../domain/counts';
import { DISCOVERY_PAGES, emptyPageOrders, nextPageOrders } from '../../domain/discovery';
import type { PageOrders } from '../../domain/discovery';
import { arrangeBy, reorderAllowed } from '../../domain/reorder';
import type { QuietGate } from '../../domain/reorder';
import { candidateFromFreeText } from '../../domain/setup';
import type { IncrementCommand } from '../../domain/commands';
import type { DiscoveryBucket, HypothesisPhase, MatchRow } from '../../types';
import { registerSettleTriggers } from '../../ui/capture/settleTriggers';
import type { CaptureIntent, GestureTarget } from '../../ui/capture/usePointerGesture';
import type { UndoRowB } from './bandB';

/**
 * B-9 — the Discovery surface's controller: state, the tap path, and the deferred-reorder
 * engine's *glue* (design spec §2.4; scenarios 8 and 11). No rendering.
 *
 * Every **decision** is imported from its one engine home, per the reuse contract:
 * `nextPageOrders`/`reorderAllowed` (DISC-7's quiet gate — scenario 11's "elevated by count,
 * never shifting under a touch"), `arrangeBy` + `DISCOVERY_PAGES` (DISC-2/DISC-4),
 * `prepareIncrement`/`commitIncrement` (the §5.1 tap path — command built at `pointerdown`,
 * ack **only** from `onCommitted`, the bus's native-`complete` seam), `applyLiveIncrement`
 * (the same fold the handler ran), `registerSettleTriggers` (the shipped trigger set),
 * `candidateFromFreeText` (the single constructor path into `toHypothesisRow`). What is B's
 * own here is React glue: one gate ref, one settle timer, one add action, and B-15's long-press
 * seam onto the hypothesis detail sheet — the same shape A's `useDiscovery` proved (its module
 * note explains every choice this file repeats), minus what other B units own (the undo pill is
 * B-11's).
 *
 * ── Rows are seeded from the shell's read, then owned here ───────────────────
 *
 * `TakeoverShell` already reads `loadDiscovery` (match + opponent + rows, one readonly
 * transaction) and re-reads it after every committed transition. The surface takes that read's
 * rows **once, at mount**, and from then on is the single writer of its own board: a committed
 * tap folds `applyLiveIncrement` locally (a re-read per tap would put one more transaction in
 * front of the next tap on the hottest surface in the app — A's documented refusal), and an add
 * re-reads through {@link refresh}. Two live copies of the counts would be a race; one owner
 * and a seed is not. Transitions never change hypothesis rows, so the seed cannot go stale
 * across the one remount boundary this surface has (breakHub ↔ discovery — the shell re-read
 * on the transition supplies fresh rows to the next mount).
 */

export interface DiscoveryPageB {
  readonly bucket: DiscoveryBucket;
  readonly rows: readonly DiscoveryHypothesisView[];
}

interface Board {
  readonly rows: readonly DiscoveryHypothesisView[];
  readonly orders: PageOrders;
}

const MODULE = 'ui-b/discovery';

/**
 * B-11's additive seams (the band's undo pill lives in the chrome; the counts live here):
 * `onRowsChanged` publishes this hook's committed row state upward whenever it changes;
 * `refreshEpoch` orders a re-read after a band undo commits (the undo rewrites sort keys this
 * hook cannot recompute without the ledger). Both optional — the surface behaves exactly as
 * before when the host passes neither.
 */
export interface DiscoverySeamsB {
  readonly onRowsChanged?: (rows: readonly UndoRowB[]) => void;
  readonly refreshEpoch?: number;
}

export function useDiscoveryB(
  match: MatchRow,
  initialRows: readonly DiscoveryHypothesisView[],
  seams: DiscoverySeamsB = {},
) {
  const { onRowsChanged, refreshEpoch } = seams;
  const [board, setBoard] = useState<Board>(() => ({
    rows: initialRows,
    // Mount is pre-interaction: the gate is open by construction, so this IS the target order.
    orders: nextPageOrders(initialRows, emptyPageOrders(), { lastTapTs: null, pointerDown: false }, Date.now()),
  }));
  const [pageIndex, setPageIndex] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  /**
   * B-15 — the hypothesis the detail sheet is open on, or `null`. Held here rather than in the
   * surface because the 450 ms hold that opens it is one of this hook's gesture callbacks, and
   * the gesture layer hands the target to the callback, not to the renderer.
   */
  const [detailId, setDetailId] = useState<string | null>(null);

  /**
   * DISC-7's gate, in a **ref** — A's documented reason holds unchanged: a timer callback reads
   * it and pointer handlers write it, and a stale closure over rendered state is exactly how a
   * settle would be judged against a tap that has already happened. Nothing renders from it.
   */
  const gate = useRef<QuietGate>({ lastTapTs: null, pointerDown: false });
  const settleTimer = useRef<number | undefined>(undefined);

  const applyRows = useCallback((rows: readonly DiscoveryHypothesisView[]): void => {
    setBoard((prev) => ({
      rows,
      orders: nextPageOrders(rows, prev.orders, gate.current, Date.now()),
    }));
  }, []);

  /** `scheduleSettle` re-arms itself; the ref breaks the definition cycle (A's device). */
  const scheduleSettleRef = useRef<() => void>(() => undefined);

  /**
   * The settle scheduler — the only code that applies a deferred reorder, always by asking
   * `reorderAllowed` against the same gate (re-evaluate, never bypass; `settleTriggers.ts`).
   */
  const scheduleSettle = useCallback((): void => {
    if (settleTimer.current !== undefined) window.clearTimeout(settleTimer.current);
    settleTimer.current = undefined;
    // A finger is down: shut for a reason no timeout can wait out. The release path re-schedules.
    if (gate.current.pointerDown) return;

    const last = gate.current.lastTapTs;
    const delay = last === null ? 0 : Math.max(0, last + config.REORDER_QUIET_MS - Date.now());
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = undefined;
      setBoard((prev) => ({
        rows: prev.rows,
        orders: nextPageOrders(prev.rows, prev.orders, gate.current, Date.now()),
      }));
      // Re-arm when the gate was still shut — a settle refused with no follow-up is a page that
      // never applies DISC-4 again. Asked of `reorderAllowed`, never re-derived arithmetic.
      if (!reorderAllowed(gate.current, Date.now())) scheduleSettleRef.current();
    }, delay);
  }, []);

  scheduleSettleRef.current = scheduleSettle;

  useEffect(
    () => () => {
      if (settleTimer.current !== undefined) window.clearTimeout(settleTimer.current);
    },
    [],
  );

  /** DISC-7's lifecycle triggers — the shipped set, shared with A's surfaces (one registrar). */
  useEffect(() => registerSettleTriggers(scheduleSettle), [scheduleSettle]);

  /** Re-read after a row-set change (an add). Failure is surfaced, never swallowed (NFR-8). */
  const refresh = useCallback(async (): Promise<void> => {
    const context = await loadDiscovery(match.id);
    // A vanished match is the shell's boundary to handle (its own next read navigates); an
    // empty surface for one frame beats this hook second-guessing the host's navigation.
    if (context === undefined) return;
    applyRows(context.rows);
  }, [applyRows, match.id]);

  // ── B-11's seams: publish committed rows up; re-read on the band's undo epoch ─

  /** Published on every committed change (seed, fold, add, refresh) — the pill's freshness. */
  useEffect(() => {
    onRowsChanged?.(board.rows);
  }, [board.rows, onRowsChanged]);

  /**
   * A band undo committed: re-read and republish. Guarded by the last-seen epoch so the
   * effect's other dependency (`refresh` re-created on a match change) can never trigger a
   * spurious read.
   */
  const seenEpoch = useRef(refreshEpoch ?? 0);
  useEffect(() => {
    const epoch = refreshEpoch ?? 0;
    if (epoch === seenEpoch.current) return;
    seenEpoch.current = epoch;
    refresh().catch((cause: unknown) => {
      const message = cause instanceof Error ? cause.message : String(cause);
      setNotice(message);
      log.error(MODULE, 'the surface could not be re-read after an undo', { message });
    });
  }, [refreshEpoch, refresh]);

  // ── The tap path (§5.1; scenario 8) ────────────────────────────────────────

  /**
   * Pointerdown: pre-build the whole command (id minted, minute derived) so no settle, reorder
   * or re-render between down and up can retarget the tap. Returning `null` declines the
   * contact — B keeps A's `canCapture` UI gate (the engine deliberately has no state guard on
   * `Increment`; "taps before kickoff create nothing" is this line).
   */
  const onPressStart = useCallback(
    (target: GestureTarget, at: number): CaptureIntent<IncrementCommand> | null => {
      if (!canCapture(match)) return null;
      target.element.dataset.pressed = 'true';
      return { kind: 'increment', command: prepareIncrement({ match, hypothesisId: target.hid, at }) };
    },
    [match],
  );

  const onPressEnd = useCallback((target: GestureTarget): void => {
    delete target.element.dataset.pressed;
  }, []);

  const onActiveChange = useCallback(
    (pointerDown: boolean): void => {
      gate.current = { ...gate.current, pointerDown };
      if (!pointerDown) scheduleSettle();
    },
    [scheduleSettle],
  );

  /**
   * B-15 — §2.4's *"long-press → hypothesis detail sheet"*. Fires from the gesture layer's own
   * timer while the finger is still down, and `classifyPointerEnd` then reports `longPress`
   * rather than `tap`, so the contact that opened the sheet **cannot also record** (the same
   * contract A's hold-to-talk rides). The target is the row resolved at `pointerdown`, so no
   * settle between down and hold can re-aim it.
   */
  const onLongPress = useCallback((target: GestureTarget): void => {
    setDetailId(target.hid);
  }, []);

  /**
   * Pointerup on a completed tap: dispatch the pre-built command; the **only** acknowledgment
   * is `onCommitted`, invoked synchronously inside the native IndexedDB `complete` listener
   * (SES-2 — no optimistic path, no pending state, no timeout that acks anyway). The flash is
   * applied imperatively on the element captured at pointerdown: identity, not position.
   */
  const onTap = useCallback(
    (target: GestureTarget, intent: CaptureIntent<IncrementCommand>, at: number): void => {
      gate.current = { ...gate.current, lastTapTs: at };
      scheduleSettle();
      if (intent.kind !== 'increment') return;
      const command = intent.command;

      void commitIncrement(command, () => {
        // Scenario 8's unmissable ack, at the commit moment. Remove → reflow → set, so a second
        // tap inside ACK_PERSIST_MIN_MS restarts the animation instead of showing nothing.
        const element = target.element;
        delete element.dataset.ack;
        void element.offsetWidth;
        element.dataset.ack = 'true';
        window.setTimeout(() => {
          delete element.dataset.ack;
        }, config.ACK_PERSIST_MIN_MS);
      }).then(
        (result) => {
          if (!result.ok) {
            setNotice(result.message);
            return;
          }
          // The count the row shows is the count that committed: the same `applyLiveIncrement`
          // the handler ran, after its transaction completed — never a per-tap re-read.
          setBoard((prev) => {
            const rows = prev.rows.map((row) =>
              row.id === command.hypothesisId
                ? {
                    ...row,
                    ...applyLiveIncrement(row, command.observation.createdAt),
                    firstObservationCreatedAt:
                      row.firstObservationCreatedAt ?? command.observation.createdAt,
                  }
                : row,
            );
            return { rows, orders: nextPageOrders(rows, prev.orders, gate.current, Date.now()) };
          });
        },
        (cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'an increment failed and was rolled back', { message });
        },
      );
    },
    [scheduleSettle],
  );

  // ── Add a pattern (DISC-5; the sheet renders only behind `canAddPattern`) ──

  /**
   * Synchronous in-flight guard, checked before any await: `busy` state disables a control only
   * after React commits the render, and a double-tapped Save lands inside that gap (the U-7
   * finding A's queue exists for). B-9 has exactly one action, so a ref is the whole queue.
   */
  const adding = useRef(false);

  const addPattern = useCallback(
    (text: string, phase: HypothesisPhase): void => {
      if (adding.current) return;
      adding.current = true;
      void (async () => {
        try {
          const result = await addDiscoveryHypothesis(
            match.id,
            candidateFromFreeText(newId(), text, phase),
          );
          if (!result.ok) {
            setNotice(result.message);
            return;
          }
          setSheetOpen(false);
          // The new row appears on its bucket's page — at the tail while the gate is shut
          // (DISC-7's `holdOrder` appends newcomers; it settles into DISC-4 order later).
          await refresh();
        } catch (cause) {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'DISC-5: a pattern could not be added', { message });
        } finally {
          adding.current = false;
        }
      })();
    },
    [match.id, refresh],
  );

  // ── Paging (§2.4 — tap-to-switch segments; page entry re-evaluates the gate) ─

  const showPage = useCallback(
    (index: number): void => {
      setPageIndex(index);
      scheduleSettle();
    },
    [scheduleSettle],
  );

  const pages = useMemo(
    (): readonly DiscoveryPageB[] =>
      DISCOVERY_PAGES.map((bucket) => ({
        bucket,
        rows: arrangeBy(
          board.rows.filter((row) => row.discoveryBucket === bucket),
          board.orders[bucket],
        ),
      })),
    [board],
  );

  /** B-15 — the row the detail sheet renders, re-derived per render so its count stays live. */
  const detailRow = useMemo(
    () => (detailId === null ? null : (board.rows.find((row) => row.id === detailId) ?? null)),
    [board.rows, detailId],
  );

  return {
    state: {
      pages,
      pageIndex,
      capturing: canCapture(match),
      notice,
      sheetOpen,
      detailRow,
    },
    gestures: { onPressStart, onTap, onLongPress, onPressEnd, onActiveChange },
    actions: {
      showPage,
      addPattern,
      openSheet: () => {
        setSheetOpen(true);
      },
      closeSheet: () => {
        setSheetOpen(false);
      },
      closeDetail: () => {
        setDetailId(null);
      },
      /** B-15 — a status override committed from the detail sheet; the row's status re-reads. */
      reread: () => {
        refresh().catch((cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'the surface could not be re-read after a detail-sheet write', {
            message,
          });
        });
      },
      dismissNotice: () => {
        setNotice(null);
      },
    },
  };
}

export type DiscoveryGesturesB = ReturnType<typeof useDiscoveryB>['gestures'];
export type DiscoveryActionsB = ReturnType<typeof useDiscoveryB>['actions'];
